import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PAYMENT_METHOD_LABELS, formatMoney, formatOrderNumber } from '@dezza/shared';
import { toast } from '@dezza/ui';
import { api } from './api';
import { API_ORIGIN } from './platform';
import { useAuth } from './auth';

/** Abre a tela do pedido a partir do aviso (mesmo comportamento do aviso de novo pedido). */
function goTo(path: string) {
  window.location.assign(path);
}

/** Avisa a lista de pedidos para destacar o pedido que chegou ou foi pago (features/orders/lib/activity.ts). */
function orderActivity(orderId: string | undefined, kind: 'created' | 'paid') {
  if (orderId) window.dispatchEvent(new CustomEvent('dezza:order-activity', { detail: { orderId, kind } }));
}

function playChime() {
  try {
    const ctx = new AudioContext();
    const notes = [880, 1320];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + i * 0.16);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + i * 0.16 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + i * 0.16 + 0.3);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + i * 0.16);
      osc.stop(ctx.currentTime + i * 0.16 + 0.32);
    });
  } catch {
    /* sem áudio */
  }
}

/**
 * Conexão em tempo real (SSE) com a API: novos pedidos do catálogo, mudanças
 * de status, estoque baixo e notificações. Reconecta sozinho com backoff.
 */
export function useRealtime(): void {
  const qc = useQueryClient();
  const { me } = useAuth();
  const companyId = me?.company?.id;
  const soundOn = me?.company?.settings.general.soundOnNewOrder ?? true;

  useEffect(() => {
    if (!companyId) return;
    let source: EventSource | null = null;
    let retry = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;

    const connect = async () => {
      try {
        const { ticket } = await api.post<{ ticket: string }>('/events/ticket', undefined, { silent: true });
        if (stopped) return;
        source = new EventSource(`${API_ORIGIN}/api/events?ticket=${encodeURIComponent(ticket)}`);
        source.addEventListener('ready', () => {
          retry = 0;
        });
        source.addEventListener('order.created', (e) => {
          const data = JSON.parse((e as MessageEvent).data) as { orderId?: string; number?: number; source?: string };
          qc.invalidateQueries({ queryKey: ['orders'] });
          qc.invalidateQueries({ queryKey: ['dashboard'] });
          qc.invalidateQueries({ queryKey: ['finance', 'cash'] });
          if (data.source === 'catalog') {
            toast.success(`Novo pedido ${data.number ? formatOrderNumber(data.number) : ''} no catálogo!`, {
              action: { label: 'Ver', onClick: () => goTo(data.orderId ? `/pedidos/${data.orderId}` : '/pedidos') },
            });
            if (soundOn) playChime();
            orderActivity(data.orderId, 'created');
          }
        });
        source.addEventListener('order.updated', () => {
          qc.invalidateQueries({ queryKey: ['orders'] });
          qc.invalidateQueries({ queryKey: ['payments'] });
          qc.invalidateQueries({ queryKey: ['dashboard'] });
          qc.invalidateQueries({ queryKey: ['finance', 'cash'] });
        });
        // Pagamento online confirmado (Pix, cartão ou boleto pelo site): aviso com som e destaque na lista.
        source.addEventListener('payment.received', (e) => {
          const data = JSON.parse((e as MessageEvent).data) as { orderId?: string; number?: number; amountCents?: number; method?: string; sound?: boolean };
          qc.invalidateQueries({ queryKey: ['orders'] });
          qc.invalidateQueries({ queryKey: ['payments'] });
          qc.invalidateQueries({ queryKey: ['dashboard'] });
          qc.invalidateQueries({ queryKey: ['finance'] });
          const method = data.method && data.method in PAYMENT_METHOD_LABELS ? PAYMENT_METHOD_LABELS[data.method as keyof typeof PAYMENT_METHOD_LABELS] : null;
          toast.success(`Pagamento recebido${data.number ? `: pedido ${formatOrderNumber(data.number)}` : ''}`, {
            description: [method, typeof data.amountCents === 'number' ? formatMoney(data.amountCents) : null].filter(Boolean).join(' · ') || undefined,
            action: { label: 'Ver pedido', onClick: () => goTo(data.orderId ? `/pedidos/${data.orderId}` : '/pedidos') },
          });
          if (soundOn && data.sound !== false) playChime();
          orderActivity(data.orderId, 'paid');
        });
        // Abertura, sangria/suprimento e fechamento de caixa feitos em outro aparelho.
        source.addEventListener('cash.updated', () => {
          qc.invalidateQueries({ queryKey: ['finance', 'cash'] });
        });
        source.addEventListener('stock.low', () => {
          qc.invalidateQueries({ queryKey: ['products'] });
          qc.invalidateQueries({ queryKey: ['stock'] });
        });
        source.addEventListener('notification.created', () => {
          qc.invalidateQueries({ queryKey: ['notifications'] });
          qc.invalidateQueries({ queryKey: ['me'] });
        });
        // Plano, status da loja ou as minhas permissões mudaram: menu e bloqueios se ajustam na hora.
        for (const type of ['member.updated', 'subscription.updated', 'company.updated']) {
          source.addEventListener(type, () => {
            qc.invalidateQueries({ queryKey: ['me'] });
            qc.invalidateQueries({ queryKey: ['billing'] });
          });
        }
        source.onerror = () => {
          source?.close();
          scheduleReconnect();
        };
      } catch {
        scheduleReconnect();
      }
    };
    const scheduleReconnect = () => {
      if (stopped) return;
      retry += 1;
      timer = setTimeout(connect, Math.min(60_000, 1_000 * 2 ** Math.min(retry, 6)));
    };
    void connect();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      source?.close();
    };
  }, [companyId, qc, soundOn]);
}
